import { zodResolver } from '@hookform/resolvers/zod';
import { Eye, EyeOff, LockKeyhole, Mail } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useLocation, useNavigate } from 'react-router';
import { toast } from 'sonner';
import { z } from 'zod';

import { api, unwrap, type Authenticated } from '@/api/client';
import { cuentaRestringidaDe, legacyDeBeta, mensajeDe } from '@/api/errors';
import type { components } from '@/api/schema';
import { useAuth } from '@/auth/AuthProvider';
import { recaptchaHeader, recaptchaToken } from '@/auth/recaptcha';
import { tokenConfianza } from '@/lib/device';

import { AuthLayout, AvisoRecaptcha, Campo } from './AuthLayout';
import { AvisoNoInvitado } from './AvisoNoInvitado';
import { DialogoCuentaRestringida } from './DialogoCuentaRestringida';

type Challenge = components['schemas']['ChallengeRequired'];

const schema = z.object({
  email: z.string().trim().min(1, 'Escribe tu correo').email('Escribe un correo válido'),
  password: z.string().min(1, 'Escribe tu contraseña'),
});
type Form = z.infer<typeof schema>;

const AVISOS: Record<string, string> = {
  SESSION_REVOKED: 'Tu sesión se abrió en otro dispositivo. Si no fuiste tú, cambia tu contraseña.',
  SESSION_EXPIRED: 'Tu sesión terminó. Inicia sesión de nuevo.',
};

export function LoginPage() {
  const {
    signIn,
    endedReason,
    clearEndedReason,
    restriccion,
    mostrarRestriccion,
    cerrarRestriccion,
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [ver, setVer] = useState(false);
  const [noInvitado, setNoInvitado] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<Form>({ resolver: zodResolver(schema) });

  useEffect(() => {
    const aviso = endedReason ? AVISOS[endedReason] : undefined;
    if (aviso) toast.warning(aviso, { id: 'sesion-terminada', duration: 8000 });
    if (endedReason) clearEndedReason();
  }, [endedReason, clearEndedReason]);

  const onSubmit = async (form: Form) => {
    try {
      const token = await recaptchaToken('login');
      const result = await unwrap<Authenticated | Challenge>(
        api.POST('/api/v1/auth/login', {
          body: { ...form, trustedDeviceToken: tokenConfianza() },
          headers: recaptchaHeader(token),
        }),
      );
      if (result.status === 'authenticated') {
        signIn(result);
        const desde = (location.state as { desde?: string } | null)?.desde;
        void navigate(desde ?? '/inicio', { replace: true });
      } else {
        void navigate('/verificacion', { state: { challenge: result, proposito: 'login' } });
      }
    } catch (err) {
      const beta = legacyDeBeta(err);
      const restringida = cuentaRestringidaDe(err);
      if (restringida) mostrarRestriccion(restringida);
      else if (beta) setNoInvitado(beta);
      else toast.error(mensajeDe(err));
    }
  };

  return (
    <AuthLayout titulo="Iniciar sesión">
      {noInvitado && <AvisoNoInvitado url={noInvitado} />}
      <form onSubmit={(e) => void handleSubmit(onSubmit)(e)} className="space-y-4" noValidate>
        <Campo etiqueta="Correo electrónico" error={errors.email?.message}>
          <span className="relative block">
            <Mail
              size={18}
              className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-texto-3"
            />
            <input
              type="email"
              autoComplete="username"
              className="input-vidrio pl-10"
              placeholder="tu@correo.com"
              aria-invalid={!!errors.email}
              {...register('email')}
            />
          </span>
        </Campo>
        <Campo etiqueta="Contraseña" error={errors.password?.message}>
          <span className="relative block">
            <LockKeyhole
              size={18}
              className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-texto-3"
            />
            <input
              type={ver ? 'text' : 'password'}
              autoComplete="current-password"
              className="input-vidrio pr-11 pl-10"
              placeholder="Tu contraseña"
              aria-invalid={!!errors.password}
              {...register('password')}
            />
            <button
              type="button"
              onClick={() => setVer((v) => !v)}
              className="absolute top-1/2 right-2 -translate-y-1/2 rounded-full p-1.5 text-texto-3 hover:text-texto"
              aria-label={ver ? 'Ocultar contraseña' : 'Mostrar contraseña'}
            >
              {ver ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </span>
        </Campo>
        <div className="text-right">
          <Link to="/recuperar-contrasenia" className="text-sm font-semibold">
            ¿Has olvidado tu contraseña?
          </Link>
        </div>
        <button
          type="submit"
          className="btn-cs btn-primario w-full py-3 text-base"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Entrando…' : 'Iniciar sesión'}
        </button>
        <p className="text-center text-xs text-texto-3">
          Al iniciar sesión aceptas los{' '}
          <a href="/terminos-y-condiciones/" target="_blank" rel="noreferrer">
            Términos y condiciones
          </a>
          .
        </p>
      </form>
      <div className="mt-6 border-t border-[var(--separador)] pt-5 text-center">
        <p className="text-sm text-texto-2">¿Aún no tienes cuenta?</p>
        <Link to="/registro" className="btn-cs btn-exito mt-2 w-full">
          Regístrate gratis
        </Link>
      </div>
      <AvisoRecaptcha />
      <DialogoCuentaRestringida restriccion={restriccion} onClose={cerrarRestriccion} />
    </AuthLayout>
  );
}
